import { useState, useEffect } from "react";
import "./css/adminpayoutcompleted.css";
import { useNavigate } from "react-router-dom";
import Pagination from "../common/Pagination";
import { apiGet, apiPost } from "../../utils/apiClient";
import { buildPayoutFilterParams, getFileNameFromResponse } from "./payoutFilterParams";

const AdminPayoutCompleted = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [payouts, setPayouts] = useState([]);
  const [selectedPayouts, setSelectedPayouts] = useState([]);
  const [bulkBarBottom, setBulkBarBottom] = useState(40);
  const [modalInfo, setModalInfo] = useState(null);
  const [confirmVisible, setConfirmVisible] = useState(false);

  useEffect(() => {
    const updateBarBottom = () => {
      const footer = document.querySelector(".admin-site-footer");
      if (!footer) return;
      const footerTop = footer.getBoundingClientRect().top;
      const viewportHeight = window.innerHeight;
      setBulkBarBottom(footerTop < viewportHeight ? viewportHeight - footerTop + 16 : 40);
    };
    window.addEventListener("scroll", updateBarBottom);
    updateBarBottom();
    return () => window.removeEventListener("scroll", updateBarBottom);
  }, []);

  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // 오늘 날짜 구하기
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // 필터 상태
  const [filterMode, setFilterMode] = useState("dateRange"); // "dateRange" | "yearMonth"
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: getTodayDate(),
    year: String(new Date().getFullYear()),
    month: String(new Date().getMonth() + 1),
  });

  const yearOptions = Array.from({ length: 5 }, (_, i) =>
    String(new Date().getFullYear() - i)
  );
  const monthOptions = Array.from({ length: 12 }, (_, i) => String(i + 1));

  // 정산 완료 목록 조회
  useEffect(() => {
    loadPayouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPayouts = async (page = 0, mode = filterMode, activeFilters = filters) => {
    setLoading(true);
    setError(null);
    try {
      const params = buildPayoutFilterParams(mode, activeFilters);
      params.append("page", page);
      params.append("size", 10);

      const response = await apiGet(`/admin/payout/complete?${params.toString()}`);

      if (response.ok) {
        const data = await response.json();
        setPayouts(data.content || []);
        setCurrentPage(data.number || 0);
        setTotalPages(data.totalPages || 0);
        setTotalElements(data.totalElements || 0);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || "정산 목록 조회에 실패했습니다.");
        setPayouts([]);
      }
    } catch (err) {
      setError(err.message || "정산 목록 조회에 실패했습니다.");
      console.error("정산 목록 조회 실패: ", err);
      setPayouts([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadPayouts(page);
  };

  // 일괄 정산 취소 — 1차 모달 표시
  const handleBulkRevert = () => {
    if (selectedPayouts.length === 0) return;

    const revertedStores = payouts
      .filter((p) => selectedPayouts.includes(p.storeCode))
      .map((p) => ({ storeCode: p.storeCode, storeNm: p.storeNm, payoutFinal: p.payoutFinal }));

    let filterLabel = null;
    if (filterMode === "yearMonth" && filters.year && filters.month) {
      filterLabel = `${filters.year}년 ${filters.month}월`;
    } else if (filterMode === "dateRange") {
      if (filters.startDate && filters.endDate) filterLabel = `${filters.startDate} ~ ${filters.endDate}`;
      else if (filters.startDate) filterLabel = `${filters.startDate} 이후`;
      else if (filters.endDate) filterLabel = `${filters.endDate} 이전`;
    }

    const body = {
      storeCodes: selectedPayouts,
      ...(filterMode === "yearMonth"
        ? { year: filters.year ? Number(filters.year) : null, month: filters.month ? Number(filters.month) : null, startDate: null, endDate: null }
        : { year: null, month: null, startDate: filters.startDate || null, endDate: filters.endDate || null }),
    };

    setModalInfo({ stores: revertedStores, filterLabel, body });
  };

  // 일괄 정산 취소 — 2차 확인 후 API 호출
  const handleConfirmRevert = async () => {
    try {
      const response = await apiPost("/admin/payout/revert", modalInfo.body);

      if (response.ok) {
        setSelectedPayouts([]);
        loadPayouts(currentPage);
        setModalInfo(null);
        setConfirmVisible(false);
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.message || "정산 취소 처리에 실패했습니다.");
      }
    } catch (err) {
      console.error("정산 취소 처리 실패:", err);
      alert("정산 취소 처리에 실패했습니다.");
    }
  };

  const handleSelectAll = (e) => {
    setSelectedPayouts(e.target.checked ? payouts.map((p) => p.storeCode) : []);
  };

  const handleSelectOne = (storeCode) => {
    setSelectedPayouts((prev) =>
      prev.includes(storeCode)
        ? prev.filter((id) => id !== storeCode)
        : [...prev, storeCode]
    );
  };

  // 검색
  const handleSearch = () => {
    setCurrentPage(0);
    loadPayouts(0);
  };

  // 필터 초기화
  const handleReset = () => {
    const resetFilters = {
      startDate: "",
      endDate: getTodayDate(),
      year: String(new Date().getFullYear()),
      month: String(new Date().getMonth() + 1),
    };
    setFilterMode("dateRange");
    setFilters(resetFilters);
    setCurrentPage(0);
    loadPayouts(0, "dateRange", resetFilters);
  };

  // 엑셀 다운로드
  const handleExcelDownload = async () => {
    try {
      const params = buildPayoutFilterParams(filterMode, filters);
      params.append("type", "COMPLETED");

      const response = await apiGet(`/admin/payout/excel?${params.toString()}`);

      if (response.ok) {
        const fileName = getFileNameFromResponse(response);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        alert("엑셀 다운로드에 실패했습니다.");
      }
    } catch (err) {
      console.error("엑셀 다운로드 실패:", err);
      alert("엑셀 다운로드에 실패했습니다.");
    }
  };

  return (
    <div className="adminpayout-completed-container">
      <div className="adminpayout-completed-wrap" style={selectedPayouts.length > 0 ? { paddingBottom: 100 } : undefined}>
        {/* 탭 네비게이션 */}
        <div className="adminpayout-completed-tab-navigation">
          <button
            className="adminpayout-completed-tab-btn"
            onClick={() => {
              navigate("/admin/payout");
            }}
          >
            정산 대기
          </button>
          <button className="adminpayout-completed-tab-btn adminpayout-completed-tab-active">
            정산 완료
          </button>
        </div>

        {/* 필터 영역 */}
        <div className="adminpayout-completed-filter-wrap">
          <span className="adminpayout-completed-filter-criteria">결제일 기준</span>
          <div className="adminpayout-completed-filter-row">
            <div className="adminpayout-completed-filter-left">
              <div className="adminpayout-completed-filter-mode-toggle">
                <button
                  className={`adminpayout-completed-mode-btn ${filterMode === "dateRange" ? "adminpayout-completed-mode-active" : ""}`}
                  onClick={() => setFilterMode("dateRange")}
                >
                  날짜 범위
                </button>
                <button
                  className={`adminpayout-completed-mode-btn ${filterMode === "yearMonth" ? "adminpayout-completed-mode-active" : ""}`}
                  onClick={() => setFilterMode("yearMonth")}
                >
                  년월
                </button>
              </div>
              <div className="adminpayout-completed-filter-group">
                {filterMode === "dateRange" ? (
                  <>
                    <input
                      type="date"
                      className="adminpayout-completed-date-input"
                      value={filters.startDate}
                      onChange={(e) =>
                        setFilters({ ...filters, startDate: e.target.value })
                      }
                    />
                    <span className="adminpayout-completed-date-separator">~</span>
                    <input
                      type="date"
                      className="adminpayout-completed-date-input"
                      value={filters.endDate}
                      onChange={(e) =>
                        setFilters({ ...filters, endDate: e.target.value })
                      }
                    />
                  </>
                ) : (
                  <>
                    <select
                      className="adminpayout-completed-select"
                      value={filters.year}
                      onChange={(e) =>
                        setFilters({ ...filters, year: e.target.value })
                      }
                    >
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>{y}년</option>
                      ))}
                    </select>
                    <select
                      className="adminpayout-completed-select"
                      value={filters.month}
                      onChange={(e) =>
                        setFilters({ ...filters, month: e.target.value })
                      }
                    >
                      {monthOptions.map((m) => (
                        <option key={m} value={m}>{m}월</option>
                      ))}
                    </select>
                  </>
                )}
              </div>
            </div>
            <div className="adminpayout-completed-search-group">
              <button
                className="adminpayout-completed-search-btn"
                onClick={handleSearch}
              >
                검색
              </button>
              <button
                className="adminpayout-completed-reset-btn"
                onClick={handleReset}
              >
                초기화
              </button>
            </div>
          </div>
        </div>

        {/* 테이블 영역 */}
        <div className="adminpayout-completed-table-wrap">
          <div className="adminpayout-completed-table-header">
            <div className="adminpayout-completed-table-info">
              총 <b>{totalElements}</b>건
            </div>
            <button
              className="adminpayout-completed-btn-excel"
              onClick={handleExcelDownload}
            >
              엑셀 다운로드
            </button>
          </div>

          {loading ? (
            <div className="adminpayout-completed-loading">로딩 중</div>
          ) : error ? (
            <div className="adminpayout-completed-error">{error}</div>
          ) : (
            <table className="adminpayout-completed-table">
              <thead>
                <tr>
                  <th className="adminpayout-completed-th-checkbox">
                    <input
                      type="checkbox"
                      checked={payouts.length > 0 && selectedPayouts.length === payouts.length}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>가맹코드</th>
                  <th>가맹점명</th>
                  <th>총 매출액</th>
                  <th>PG 수수료</th>
                  <th>자사 수수료</th>
                  <th>실 정산액</th>
                  <th>기타</th>
                </tr>
              </thead>
              <tbody>
                {payouts.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center", fontWeight: "bold" }}>
                      등록된 정산 내역이 없습니다.
                    </td>
                  </tr>
                ) : (
                  payouts.map((payout) => (
                    <tr
                      key={payout.storeCode}
                      className={selectedPayouts.includes(payout.storeCode) ? "adminpayout-completed-row-selected" : ""}
                    >
                      <td className="adminpayout-completed-td-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedPayouts.includes(payout.storeCode)}
                          onChange={() => handleSelectOne(payout.storeCode)}
                        />
                      </td>
                      <td>{payout.storeCode}</td>
                      <td>{payout.storeNm}</td>
                      <td>{payout.payoutPrice?.toLocaleString()}원</td>
                      <td>{payout.payoutBaseTax?.toLocaleString()}원</td>
                      <td>{payout.payoutCustomTax?.toLocaleString()}원</td>
                      <td>{payout.payoutFinal?.toLocaleString()}원</td>
                      <td>
                        <button
                          className="adminpayout-completed-btn-detail"
                          onClick={() => navigate(`/admin/payout/store/${payout.storeCode}`, { state: { storeName: payout.storeNm, filterMode, filters, returnPath: "/admin/payout/completed" } })}
                        >
                          상세
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {/* 페이지네이션 */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      {selectedPayouts.length > 0 && (
        <div className="adminpayout-completed-bulk-bar" style={{ bottom: bulkBarBottom }}>
          <span className="adminpayout-completed-bulk-count">
            {selectedPayouts.length}개 선택됨
          </span>
          <div className="adminpayout-completed-bulk-bar-actions">
            <button
              className="adminpayout-completed-btn-bulk"
              onClick={handleBulkRevert}
            >
              일괄 정산 취소
            </button>
            <button
              className="adminpayout-completed-btn-cancel"
              onClick={() => setSelectedPayouts([])}
            >
              취소
            </button>
          </div>
        </div>
      )}

      {modalInfo && (
        <div className="adminpayout-completed-modal-overlay" onClick={() => setModalInfo(null)}>
          <div className="adminpayout-completed-modal" onClick={(e) => e.stopPropagation()}>
            <div className="adminpayout-completed-modal-header">
              <span className="adminpayout-completed-modal-title">정산 취소</span>
              <button className="adminpayout-completed-modal-close" onClick={() => setModalInfo(null)}>✕</button>
            </div>
            {modalInfo.filterLabel && (
              <div className="adminpayout-completed-modal-period">
                기간: {modalInfo.filterLabel}
              </div>
            )}
            <div className="adminpayout-completed-modal-table-wrap">
              <table className="adminpayout-completed-modal-table">
                <thead>
                  <tr>
                    <th>가맹코드</th>
                    <th>가맹점명</th>
                    <th>정산액</th>
                  </tr>
                </thead>
                <tbody>
                  {modalInfo.stores.map((s) => (
                    <tr key={s.storeCode}>
                      <td>{s.storeCode}</td>
                      <td>{s.storeNm}</td>
                      <td>{s.payoutFinal?.toLocaleString()}원</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="adminpayout-completed-modal-summary">
              총 {modalInfo.stores.length}건 /&nbsp;
              {modalInfo.stores.reduce((sum, s) => sum + (s.payoutFinal ?? 0), 0).toLocaleString()}원
              &nbsp;정산 취소 하시겠습니까?
            </div>
            <div className="adminpayout-completed-modal-footer">
              <button className="adminpayout-completed-modal-confirm" onClick={() => setConfirmVisible(true)}>
                확인
              </button>
              <button className="adminpayout-completed-modal-cancel-btn" onClick={() => setModalInfo(null)}>
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmVisible && modalInfo && (
        <div className="adminpayout-completed-modal-overlay" onClick={() => setConfirmVisible(false)}>
          <div className="adminpayout-completed-modal adminpayout-completed-modal-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="adminpayout-completed-modal-header">
              <span className="adminpayout-completed-modal-title">최종 확인</span>
              <button className="adminpayout-completed-modal-close" onClick={() => setConfirmVisible(false)}>✕</button>
            </div>
            <p className="adminpayout-completed-modal-confirm-text">
              선택한 {modalInfo.stores.length}건을 정산 취소 처리합니다.<br />진행하시겠습니까?
            </p>
            <div className="adminpayout-completed-modal-footer">
              <button className="adminpayout-completed-modal-confirm" onClick={handleConfirmRevert}>
                확인
              </button>
              <button className="adminpayout-completed-modal-cancel-btn" onClick={() => setConfirmVisible(false)}>
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPayoutCompleted;
